import { DatePicker, Input, InputNumber, Modal, Select, message } from "antd";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { StockPrice, ratioToStockPricePct } from "fin-web-components";
import { SearchStockWithCatalog } from "../../components/fin-web/SearchStockWithCatalog";
import { queryStockDetailInfoForLongfans } from "../../components/fin-web/stockDetailQuery";
import type { StockSearchBrief } from "../../global/GlobalType";
import { UserHoldingsApi } from "./UserHoldingsApi";
import "./UserHoldingsPage.css";

const RECT_BTN = "user-holdings-rect-modal-btn";
const REASON_OPTS = ["技术面", "消息面", "基本面"];
const DEFAULT_BUY_SHARES = 10000;
const DEFAULT_REASON_TYPE = REASON_OPTS[0];

/** 无后端建议时的兜底：9:30–15:00（工作日）用当前，否则上一工作日 15:00（不含法定假日） */
function fallbackDefaultBuyTime(now: Dayjs = dayjs()): Dayjs {
  const minuteOfDay = now.hour() * 60 + now.minute();
  const inSession = minuteOfDay >= 9 * 60 + 30 && minuteOfDay <= 15 * 60;
  const dow = now.day(); // 0 Sun .. 6 Sat
  const weekday = dow >= 1 && dow <= 5;
  if (weekday && inSession) {
    return now;
  }
  let d = now.startOf("day");
  do {
    d = d.subtract(1, "day");
  } while (d.day() === 0 || d.day() === 6);
  return d.hour(15).minute(0).second(0).millisecond(0);
}

function resolveDefaultBuyTime(suggestedBuyTime?: string | null): Dayjs {
  const suggested = String(suggestedBuyTime ?? "").trim();
  if (suggested) {
    const d = dayjs(suggested, "YYYY-MM-DD HH:mm:ss", true);
    if (d.isValid()) {
      return d;
    }
  }
  return fallbackDefaultBuyTime();
}

function ReasonDetailWithImport(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  maxLength?: number;
  rows?: number;
  placeholder?: string;
  stockCode?: string;
  onImportClick?: () => void;
}): JSX.Element {
  const {
    label,
    value,
    onChange,
    maxLength = 300,
    rows = 4,
    placeholder,
    stockCode,
    onImportClick,
  } = props;
  return (
    <div className="user-holding-form-block" style={{ marginTop: 12 }}>
      <div className="uh-reason-detail-head">
        <span className="user-holding-lab">{label}</span>
        {stockCode && onImportClick ? (
          <button type="button" className="uh-reason-import-btn" onClick={onImportClick}>
            从笔记中导入
          </button>
        ) : null}
      </div>
      <Input.TextArea
        placeholder={placeholder}
        maxLength={maxLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="user-holding-input"
      />
    </div>
  );
}

/** 建仓买入 */
export function OpenBuyModal(props: {
  spaceId: string;
  open: boolean;
  seedStock?: StockSearchBrief | null;
  /** 打开时预填成交价（如策略页当前股价） */
  seedBuyPrice?: number | null;
  /** 打开时预填涨跌幅（小数或已是展示口径均可，配合 StockPrice） */
  seedChangePct?: number | null;
  /** 建议买入时间（盘外为上一交易日 15:00）；缺省则用当前时刻 */
  suggestedBuyTime?: string | null;
  /** 已有持仓股票代码（同股再建仓时展示分仓提示） */
  heldStockCodes?: string[];
  /** 强制展示分仓提示（如从已有持仓行点建仓） */
  forceLotTip?: boolean;
  onOpenNoteImport?: (stockCode: string, stockName: string, onPick: (content: string) => void) => void;
  onClose: (ok: boolean) => void;
  /** @deprecated 改用 heldStockCodes 判重；保留兼容 */
  holdingsCount?: number;
  /** 为 true 时不 toast「已建仓」（由调用方自定义成功反馈） */
  silentSuccess?: boolean;
}): JSX.Element {
  const {
    spaceId,
    open,
    seedStock,
    seedBuyPrice,
    seedChangePct,
    suggestedBuyTime,
    heldStockCodes,
    forceLotTip,
    onOpenNoteImport,
    onClose,
    holdingsCount,
    silentSuccess,
  } = props;
  const [picked, setPicked] = useState<StockSearchBrief | null>(null);
  const [buyPrice, setBuyPrice] = useState<number>();
  const [spotPrice, setSpotPrice] = useState<number>();
  const [spotChangePct, setSpotChangePct] = useState<number | null>(null);
  const [buyShares, setBuyShares] = useState<number | undefined>(DEFAULT_BUY_SHARES);
  const [reasonType, setReasonType] = useState(DEFAULT_REASON_TYPE);
  const [reasonDetail, setReasonDetail] = useState("");
  const [buyTime, setBuyTime] = useState<Dayjs | null>(() => resolveDefaultBuyTime(null));
  const wasOpenRef = useRef(false);
  /** 选股后拉价请求序号，避免快速换股时旧请求覆盖新价 */
  const priceFetchSeqRef = useRef(0);

  const heldSet = useMemo(() => {
    const s = new Set<string>();
    (heldStockCodes ?? []).forEach((c) => {
      const t = String(c ?? "").trim().toUpperCase();
      if (t) s.add(t);
    });
    return s;
  }, [heldStockCodes]);

  const showLotTip =
    !!forceLotTip ||
    (!!picked?.code && heldSet.has(String(picked.code).trim().toUpperCase()));

  const fillLatestBuyPrice = async (stockCode: string) => {
    const code = stockCode.trim();
    if (!code) {
      return;
    }
    const seq = ++priceFetchSeqRef.current;
    const detail = await queryStockDetailInfoForLongfans(code);
    if (seq !== priceFetchSeqRef.current) {
      return;
    }
    const px = detail?.latestPrice;
    if (typeof px === "number" && Number.isFinite(px) && px > 0) {
      setBuyPrice(px);
      setSpotPrice(px);
    }
    if (typeof detail?.priceChange === "number" && Number.isFinite(detail.priceChange)) {
      setSpotChangePct(detail.priceChange);
    }
  };

  // 仅在弹框从关闭→打开时初始化；避免父组件因 K 线按键重渲染导致 seedStock 引用变化而清空表单
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setBuyTime(resolveDefaultBuyTime(suggestedBuyTime));
      setBuyShares(DEFAULT_BUY_SHARES);
      setReasonDetail("");
      setReasonType(DEFAULT_REASON_TYPE);
      setSpotChangePct(
        typeof seedChangePct === "number" && Number.isFinite(seedChangePct) ? seedChangePct : null
      );
      if (seedStock?.code) {
        setPicked(seedStock);
      } else {
        setPicked(null);
      }
      if (typeof seedBuyPrice === "number" && Number.isFinite(seedBuyPrice) && seedBuyPrice > 0) {
        setBuyPrice(seedBuyPrice);
        setSpotPrice(seedBuyPrice);
        if (seedStock?.code && (seedChangePct == null || !Number.isFinite(seedChangePct))) {
          void fillLatestBuyPrice(seedStock.code);
        }
      } else {
        setBuyPrice(undefined);
        setSpotPrice(undefined);
        if (seedStock?.code) {
          void fillLatestBuyPrice(seedStock.code);
        }
      }
    }
    wasOpenRef.current = open;
  }, [open, seedStock, seedBuyPrice, seedChangePct, suggestedBuyTime]);

  // 弹框打开时截断所有按键，避免背后 K 线抢走快捷键
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => e.stopPropagation();
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const ok = async () => {
    const codeKey = String(picked?.code ?? "").trim().toUpperCase();
    const alreadyHeld = !!codeKey && heldSet.has(codeKey);
    const distinctCount = heldSet.size > 0 ? heldSet.size : holdingsCount ?? 0;
    if (!alreadyHeld && distinctCount >= 50) {
      message.warning("最多只允许添加50只持仓中的股票哦！您可以先卖出后再添加新持仓");
      return;
    }
    if (!picked?.code) {
      message.error("请选择标的");
      return;
    }
    if (!buyShares || !(buyPrice && buyPrice > 0)) {
      message.error("请填写成交价与股数");
      return;
    }
    const bt = (buyTime ?? dayjs()).format("YYYY-MM-DD HH:mm:ss");
    const err = await UserHoldingsApi.openBuyTry(spaceId, {
      stockCode: picked.code,
      stockName: picked.name,
      buyPrice,
      buyShares,
      buyTime: bt,
      reasonType,
      reasonDetail,
    });
    if (!err) {
      if (!silentSuccess) {
        message.success("已建仓");
      }
      onClose(true);
      return;
    }
    message.error(err);
  };

  return (
    <Modal
      open={open}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span>{forceLotTip ? "加仓" : "建仓买入"}</span>
          {showLotTip ? (
            <span style={{ color: "#cf1322", fontSize: 12, fontWeight: 300 }}>
              相同股票加仓将会作为独立仓位，这样管理更清楚
            </span>
          ) : null}
        </div>
      }
      onCancel={() => onClose(false)}
      width={620}
      okText="确认"
      cancelText="取消"
      okButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
      cancelButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
      onOk={() => void ok()}
      destroyOnClose
    >
      <div className="user-holding-form-block" style={{ marginTop: 26 }}>
        {forceLotTip && picked ? (
          <div className="user-holding-modal-stock-head" style={{ marginBottom: 0 }}>
            <span className="user-holding-modal-stock-name">{picked.name || picked.code}</span>
            <StockPrice
              mode="one-line"
              price={spotPrice ?? buyPrice ?? 0}
              change={ratioToStockPricePct(spotChangePct)}
            />
          </div>
        ) : (
          <SearchStockWithCatalog
            stockCode={picked?.code}
            borderRadius={4}
            focusBackgroundColor="#f4f5f9"
            onSelect={(s) => {
              setPicked(s);
              void fillLatestBuyPrice(s.code);
            }}
            onCleared={() => {
              setPicked(null);
              priceFetchSeqRef.current += 1;
              setBuyPrice(undefined);
            }}
          />
        )}
      </div>
      <div style={{ display: "flex", gap: 24, marginTop: 16 }}>
        <div className="user-holding-form-block" style={{ flex: 1 }}>
          <span className="user-holding-lab">成交价</span>
          <InputNumber
            className="user-holding-input"
            placeholder="成交价"
            style={{ width: "100%" }}
            value={buyPrice}
            onChange={(v) => setBuyPrice(typeof v === "number" ? v : undefined)}
            min={0}
            step={0.01}
            controls={false}
          />
        </div>
        <div className="user-holding-form-block" style={{ flex: 1 }}>
          <span className="user-holding-lab">股数</span>
          <InputNumber
            className="user-holding-input"
            placeholder="股数"
            style={{ width: "100%" }}
            value={buyShares}
            onChange={(v) => setBuyShares(typeof v === "number" ? v : undefined)}
            min={1}
            step={1}
            controls={false}
          />
        </div>
      </div>
      <div className="user-holding-form-block" style={{ marginTop: 12 }}>
        <DatePicker
          className="user-holding-input user-holding-datepicker"
          showTime
          format="YYYY-MM-DD HH:mm:ss"
          style={{ width: "100%" }}
          value={buyTime}
          onChange={(d) => setBuyTime(d)}
          placeholder="买入时间；非交易时段默认上一交易日 15:00"
          allowClear={false}
        />
      </div>
      <div className="user-holding-form-block" style={{ marginTop: 12 }}>
        <span className="user-holding-lab">理由类别</span>
        <Select
          placeholder="请选择"
          options={REASON_OPTS.map((x) => ({ label: x, value: x }))}
          value={reasonType}
          onChange={setReasonType}
          style={{ width: "100%" }}
          allowClear={false}
        />
      </div>
      <ReasonDetailWithImport
        label="具体原因"
        placeholder="最多300字"
        value={reasonDetail}
        onChange={setReasonDetail}
        stockCode={picked?.code}
        onImportClick={
          picked?.code && onOpenNoteImport
            ? () =>
                onOpenNoteImport(picked.code, picked.name ?? "", (content) =>
                  setReasonDetail(content.slice(0, 300))
                )
            : undefined
        }
      />
    </Modal>
  );
}
